import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fmtFull } from "../../shared/format.js";
import { MediaTile } from "../media/MediaTile.js";
import { Spinner } from "../../shared/ui/index.js";
import { useToast } from "../../shared/toast.js";
import { uploadMedia } from "../media/upload.js";
import { useGuestGallery, type GuestIdentity } from "./guest.api.js";
import type { MediaItem } from "../../shared/types.js";

export function GalleryView({
  eventId,
  token,
  identity,
  identityValid,
  onNeedIdentity,
}: {
  eventId: string | undefined;
  token: string | undefined;
  identity: GuestIdentity;
  identityValid: boolean;
  onNeedIdentity: () => void;
}) {
  const gallery = useGuestGallery(eventId, token);
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [busy, setBusy] = useState<{ done: number; total: number } | null>(null);

  const items = useMemo(() => gallery.data?.pages.flatMap((p) => p.items) ?? [], [gallery.data]);
  const firstPage = gallery.data?.pages[0];

  /* La galerie doit toujours être complète (dossiers par invité) : on enchaîne les pages
     tant qu'il y en a, plutôt que de plafonner à la 1re comme le ferait un simple useQuery. */
  useEffect(() => {
    if (gallery.hasNextPage && !gallery.isFetchingNextPage) {
      gallery.fetchNextPage();
    }
  }, [gallery.hasNextPage, gallery.isFetchingNextPage, gallery.fetchNextPage]);

  /* Un "dossier" par invité (regroupé par pseudo, seul identifiant visible côté invité). */
  const groups = useMemo(() => {
    const map = new Map<string, MediaItem[]>();
    for (const m of items) {
      const key = m.pseudo || "Invité";
      const arr = map.get(key);
      if (arr) arr.push(m);
      else map.set(key, [m]);
    }
    return Array.from(map.entries()).map(([pseudo, its]) => ({ pseudo, items: its }));
  }, [items]);

  async function onFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // permet de resélectionner les mêmes fichiers ensuite
    if (files.length === 0) return;
    if (!identityValid || !token || !eventId) {
      onNeedIdentity();
      return;
    }

    setBusy({ done: 0, total: files.length });
    let failed = 0;
    for (const f of files) {
      try {
        await uploadMedia({
          eventId,
          token,
          file: f,
          kind: "image",
          contentType: f.type || "image/jpeg",
          name: identity.name,
          email: identity.email,
        });
      } catch {
        failed++;
      }
      setBusy((b) => (b ? { ...b, done: b.done + 1 } : b));
    }
    setBusy(null);
    await qc.invalidateQueries({ queryKey: ["gallery", "guest", eventId, token] });

    const ok = files.length - failed;
    if (failed === 0) {
      toast(`${ok} photo${ok > 1 ? "s" : ""} envoyée${ok > 1 ? "s" : ""}`);
    } else if (ok === 0) {
      toast("Échec de l'envoi", "err");
    } else {
      toast(`${ok} envoyée${ok > 1 ? "s" : ""}, ${failed} échec${failed > 1 ? "s" : ""}`, "err");
    }
  }

  if (!token) {
    return (
      <Centered>
        Scannez le QR code de l'événement pour accéder à la galerie.
      </Centered>
    );
  }

  const uploadBar = (
    <div className="flex shrink-0 items-center justify-between gap-2 px-3 pb-3 pt-3">
      <p className="text-xs" style={{ color: "var(--theme-muted)" }}>
        {items.length > 0 ? `${items.length} photo${items.length > 1 ? "s" : ""}` : "Ajoutez vos photos"}
      </p>
      <button
        onClick={() => fileRef.current?.click()}
        disabled={!!busy}
        className="rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-60"
        style={{ background: "var(--theme-primary)", color: "var(--theme-primary-text)" }}
      >
        {busy ? `Envoi… ${busy.done}/${busy.total}` : "+ Ajouter des photos"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={onFiles}
      />
    </div>
  );

  if (gallery.isLoading) {
    return (
      <div className="flex h-full flex-col">
        {uploadBar}
        <div className="min-h-0 flex-1">
          <Centered><Spinner /></Centered>
        </div>
      </div>
    );
  }

  if (firstPage?.locked) {
    return (
      <div className="flex h-full flex-col">
        {uploadBar}
        <div className="min-h-0 flex-1">
          <Centered>
            <span className="text-4xl">🎁</span>
            <p className="mt-3 font-serif text-lg" style={{ color: "var(--theme-text)" }}>
              C'est une surprise !
            </p>
            <p className="mt-1 text-sm" style={{ color: "var(--theme-muted)" }}>
              La galerie s'ouvre le {firstPage.revealAt && fmtFull(firstPage.revealAt)}.
            </p>
          </Centered>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {uploadBar}
        <div className="min-h-0 flex-1">
          <Centered>Aucune photo pour l'instant — soyez le premier !</Centered>
        </div>
      </div>
    );
  }

  const openedGroup = openGroup ? groups.find((g) => g.pseudo === openGroup) : null;

  return (
    <div className="flex h-full flex-col">
      {uploadBar}
      <div className="sv-scroll min-h-0 flex-1 px-3 pb-3">
        {openedGroup ? (
          <>
            <button
              onClick={() => setOpenGroup(null)}
              className="mb-3 flex items-center gap-1 text-xs font-medium"
              style={{ color: "var(--theme-primary)" }}
            >
              ← Tous les invités
            </button>
            <p className="mb-2 px-1 text-sm font-medium" style={{ color: "var(--theme-text)" }}>
              {openedGroup.pseudo} · {openedGroup.items.length} photo
              {openedGroup.items.length > 1 ? "s" : ""}
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {openedGroup.items.map((m) => (
                <MediaTile key={m.id} item={m} />
              ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            {groups.map((g) => (
              <GuestFolder
                key={g.pseudo}
                pseudo={g.pseudo}
                items={g.items}
                onClick={() => setOpenGroup(g.pseudo)}
              />
            ))}
          </div>
        )}
        {gallery.isFetchingNextPage && (
          <p className="pt-3 text-center text-xs" style={{ color: "var(--theme-muted)" }}>
            Chargement des photos restantes…
          </p>
        )}
      </div>
    </div>
  );
}

function GuestFolder({
  pseudo,
  items,
  onClick,
}: {
  pseudo: string;
  items: MediaItem[];
  onClick: () => void;
}) {
  const cover = items
    .find((m) => m.status === "ready")
    ?.renditions.find((r) => r.kind === "thumb")?.url;

  return (
    <button
      onClick={onClick}
      className="flex flex-col gap-1.5 overflow-hidden rounded-xl border p-2 text-left"
      style={{ background: "var(--theme-surface)", borderColor: "var(--theme-border)" }}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-stone-100">
        {cover ? (
          <img src={cover} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-2xl">📷</div>
        )}
        <span className="absolute bottom-1 right-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
          {items.length}
        </span>
      </div>
      <span className="truncate text-xs font-medium" style={{ color: "var(--theme-text)" }}>
        {pseudo}
      </span>
    </button>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 text-center">
      <div className="text-sm" style={{ color: "var(--theme-muted)" }}>
        {children}
      </div>
    </div>
  );
}
